import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { MailCheck, MailWarning, LogIn, Home, Sparkles } from "lucide-react";

export default function Confirm() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";
  const [copied, setCopied] = useState(false);

  const copyEmail = () => {
    if (!email) return;
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-[#fff7f2] via-[#fffaf6] to-[#fef3e8] px-4 py-10">
      <div className="w-full max-w-lg">
        {/* Carte principale */}
        <div className="relative bg-background rounded-3xl shadow-2xl border border-border/60 overflow-hidden">
          {/* Bandeau dégradé */}
          <div className="h-2 bg-gradient-to-r from-[#f7bbac] to-[#f9bb72]" />

          <div className="px-8 py-10 sm:px-10 sm:py-12 space-y-7">
            {/* Icône */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-[#f7bbac]/20 blur-xl scale-150" />
                <div className="relative h-20 w-20 rounded-full bg-gradient-to-br from-[#f7bbac] to-[#f9bb72] flex items-center justify-center shadow-lg">
                  <MailCheck className="h-10 w-10 text-white" strokeWidth={2.5} />
                </div>
              </div>
            </div>

            {/* Titre */}
            <div className="text-center space-y-3">
              <h1 className="text-2xl sm:text-3xl font-display bg-gradient-to-r from-[#f7bbac] to-[#f9bb72] bg-clip-text text-transparent">
                Presque terminé&nbsp;!
              </h1>
              <p className="text-sm text-muted-foreground">
                Il ne reste plus qu'une étape pour activer votre compte.
              </p>
            </div>

            {/* Message principal */}
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-2xl p-5 border border-border/50 space-y-3">
                <p className="text-sm text-foreground leading-relaxed">
                  Un mail d'activation a été envoyé sur votre boîte mail
                  {email && (
                    <>
                      {" "}
                      <button
                        onClick={copyEmail}
                        className="inline-flex items-center gap-1 font-semibold text-[#f86f21] hover:underline break-all"
                        title="Cliquez pour copier"
                      >
                        {email}
                      </button>
                      {copied && (
                        <span className="ml-1 text-xs text-green-600 font-medium">
                          copié&nbsp;!
                        </span>
                      )}
                    </>
                  )}
                  .
                </p>

                <div className="flex items-start gap-3 pt-1">
                  <MailWarning className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    <span className="font-medium text-foreground">
                      Vérifiez également votre boîte spam
                    </span>
                    {" "}
                    — il arrive que le mail d'activation s'y glisse.
                  </p>
                </div>
              </div>

              {/* Étapes */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#f7bbac]/15 text-[#f86f21] font-semibold text-xs">
                    1
                  </span>
                  <span className="text-muted-foreground">
                    Ouvrez le mail et cliquez sur le lien d'activation
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#f7bbac]/15 text-[#f86f21] font-semibold text-xs">
                    2
                  </span>
                  <span className="text-muted-foreground">
                    Dès validation de votre compte, connectez-vous
                  </span>
                </div>
              </div>
            </div>

            {/* CTA principal */}
            <Link
              to="/users/sign_in"
              className="flex items-center justify-center gap-2 w-full rounded-xl bg-gradient-to-r from-[#f7bbac] to-[#f9bb72] text-white font-medium py-3.5 shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all"
            >
              <LogIn className="h-5 w-5" />
              Se connecter
            </Link>

            {/* Lien retour accueil */}
            <Link
              to="/"
              className="flex items-center justify-center gap-1.5 w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
            >
              <Home className="h-4 w-4" />
              Retour à l'accueil
            </Link>
          </div>
        </div>

        {/* Note en bas */}
        <p className="text-center text-xs text-muted-foreground/70 mt-6 flex items-center justify-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-[#f7bbac]" />
          Vous n'avez pas reçu le mail&nbsp;? Vérifiez l'adresse saisie ou réessayez dans quelques minutes.
        </p>
      </div>
    </div>
  );
}
